import ApiClient from 'api-client/ApiClient'
import { getDomainId, getIDPName } from 'api-client/helpers'
import type { User } from 'api-client/keystone.model'
import { defaultGroupMappingId } from 'app/constants'
import {
  addIdp,
  deleteFederatedUsers,
  deleteGroup,
  deleteGroupMapping,
  deleteIdp,
  deleteSsoConfig,
  updateIdp,
} from 'app/plugins/account/components/ssoManagement/sso/actions'
import type { DeletionStep } from 'app/plugins/account/components/ssoManagement/sso/DeletionProgress'
import { SsoProviders } from 'app/plugins/account/components/ssoManagement/sso/model'
import { extractGroupIdsForMapping } from 'app/plugins/account/components/ssoManagement/utils/mappingUtils'

export enum DELETION_STATUS {
  IDLE = 'idle',
  PENDING = 'pending',
  SUCCESS = 'success',
  WARNING = 'warning',
  FAILED = 'failed',
}

/** Thrown by a cleanup step that partially succeeded but should not block subsequent steps. */
export class SoftError extends Error {
  readonly isSoft = true
  constructor(message: string) {
    super(message)
    this.name = 'SoftError'
  }
}

export const createSsoConfigBody = (params) => ({
  provider: params.ssoProvider === SsoProviders.Other ? params.ssoProviderName : params.ssoProvider,
  entity_id: params.entityId,
  metadata_url: params.metadataUrl,
  // metadata: params.metadataUrl ? undefined : params.metadata,
  attr_map_xml: params.defaultAttributeMap,
  domain_name: getIDPName(),
})

export const handleIdpConfiguration = async (hasIdp, params) => {
  if (hasIdp) {
    await updateIdp({
      identity_provider: {
        remote_ids: [params.entityId],
      },
    })
  } else {
    await addIdp({
      identity_provider: {
        remote_ids: [params.entityId],
        description: null,
        domain_id: getDomainId(),
        enabled: true,
      },
    })
  }
}

export const getErrorMsgFromApiError = (error) => {
  const msg = error?.response?.data?.match(/"msg"\s*:\s*"([^"]*)"/)
  return msg
    ? `Failed to update SSO configuration. ${msg[1]}`
    : 'Failed to update SSO configuration. Please try again.'
}

const safeDeleteIdp = async () => {
  const { keystone } = ApiClient.getInstance()

  try {
    // Check if IDP exists before Delete
    const existingIdps = await keystone.getIdentityProviders()

    if (!existingIdps?.length) {
      // IDP already gone - mark as success
      return
    }

    await deleteIdp()
  } catch (err) {
    // If backend returns “not found”, also mark as success
    if (err?.message?.includes('Could not find Identity Provider')) return
    throw err // otherwise, throw the error to fail the step
  }
}

export const deleteGroupsAndMapping = async () => {
  // Deletes the group mapping first, then the groups.
  // Order matters: Platform9 Keystone returns 403 if you attempt to delete a group
  // that is still referenced in an active federation mapping.
  const { keystone } = ApiClient.getInstance()

  const mappings = await keystone.getGroupMappings()
  if (!mappings?.length) return

  const mappingId = defaultGroupMappingId(getIDPName())
  const groupIds = extractGroupIdsForMapping(mappings, mappingId)

  // Delete mapping first to unblock group deletion
  await deleteGroupMapping(mappingId)

  if (groupIds?.length) {
    const results = await Promise.allSettled(groupIds.map((id) => deleteGroup(id)))
    const failedIds = groupIds.filter((_, i) => results[i].status === 'rejected')
    const firstFailure = results.find((r): r is PromiseRejectedResult => r.status === 'rejected')
    if (failedIds.length) {
      // Soft-fail: signal the step runner to show a warning and continue.
      // A hard throw here would block IDP and SSO config deletion, leaving SSO partially disabled.
      throw new SoftError(
        `${failedIds.length} group(s) could not be deleted (IDs: ${failedIds.join(', ')}). Please remove them manually from the SAML Groups page. Error: ${firstFailure?.reason?.message ?? 'Unknown error'}`,
      )
    }
  }
}

export const defaultDeleteProgressDetails: DeletionStep[] = [
  { step: 1, status: 'idle', label: 'Delete Federated Users' },
  { step: 2, status: 'idle', label: 'Delete Groups and Mappings' },
  { step: 3, status: 'idle', label: 'Delete Identity Provider' },
  { step: 4, status: 'idle', label: 'Delete SSO Configuration' },
]

export const ssoCleanupSteps = [
  {
    step: 1,
    label: 'Delete Federated Users',
    action: () => deleteFederatedUsers(),
    defaultErrorMessage: 'Failed to delete federated users',
  },
  {
    step: 2,
    label: 'Delete Groups and Mappings',
    action: () => deleteGroupsAndMapping(),
    defaultErrorMessage: 'Failed to delete groups and mappings',
  },
  {
    step: 3,
    label: 'Delete Identity Provider',
    action: safeDeleteIdp,
    defaultErrorMessage: 'Failed to delete Identity Provider',
  },
  {
    step: 4,
    label: 'Delete SSO Configuration',
    action: () => deleteSsoConfig(),
    defaultErrorMessage: 'Failed to delete SSO configuration',
  },
]
const FEDERATED_USER_ID_LENGTH = 64 // UUID length

// Federated users are shadow user entries created for SSO users in Keystone
// Federated users should not be directly managed in PCD
// And should be controlled from SSO providers and SAML groups mapping in PCD
export const isFederatedUser = (user: User | null | undefined): boolean =>
  user?.id?.length === FEDERATED_USER_ID_LENGTH && !user?.default_project_id
